/**
 *
 * useSAMLAuthentication
 *
 */

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { incrementSignUpCount, processing } from 'containers/Auth/actions';
import { makeSelectSignUpCount } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { rrfConfig } from 'utils/firebaseService';
import Mixpanel from 'utils/mixpanelService';

const handleUserCredentials = async (
  userCredential,
  firestore,
  samlConfiguration,
) => {
  const userData = await firestore
    .collection('users')
    .doc(userCredential.user.uid)
    .get();
  if (!userData.exists) {
    const mappedAdditionalUserInfo = {};
    Object.keys(userCredential.additionalUserInfo.profile).forEach(key => {
      mappedAdditionalUserInfo[
        _get(samlConfiguration, ['fieldsMapping', key], key)
      ] = userCredential.additionalUserInfo.profile[key];
    });

    const {
      email,
      firstName,
      lastName,
      ...samlData
    } = mappedAdditionalUserInfo;

    const displayName = userCredential.user.displayName
      ? userCredential.user.displayName
      : firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || lastName || email;

    const profileData = {
      email: userCredential.user.email || email,
      displayName,
      firstName,
      lastName,
      samlData,
      providerData: userCredential.user.providerData,
      avatarUrl: userCredential.user.photoURL,
    };
    Object.keys(profileData).forEach(
      key => profileData[key] === undefined && delete profileData[key],
    );

    const userProfile = rrfConfig.profileFactory(
      userCredential.user,
      profileData,
    );
    await firestore
      .collection('users')
      .doc(userCredential.user.uid)
      .set(userProfile);

    if (!userCredential.user.email && userProfile.email) {
      await userCredential.user.updateEmail(userProfile.email);
    }
    Mixpanel.track('Sign Up - Successful', { authMethod: 'saml' });
    if (_get(userCredential, 'user.uid'))
      Mixpanel.alias(userCredential.user.uid);
  } else {
    Mixpanel.track('Login - Successful', { authMethod: 'saml' });
    Mixpanel.forceIdentify();
  }
};

function useSAMLAuthentication({ enableAutomatic }) {
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const clientDetails = useSelector(makeSelectClientDetails());
  const signUpCount = useSelector(makeSelectSignUpCount());

  const samlConfiguration = _get(clientDetails, 'samlConfiguration', {});

  useEffect(() => {
    if (enableAutomatic) {
      let hasResult = false;

      firebase
        .auth()
        .getRedirectResult()
        .then(result => {
          if (!_isEmpty(_get(result, 'user'))) {
            hasResult = true;

            const firestore = firebase.firestore();
            return handleUserCredentials(result, firestore, samlConfiguration);
          }
          return null;
        })
        .catch(error => {
          Mixpanel.track('SAML Redirect Authentication - Failed', {
            errorCode: _get(error, 'code', 'undefined'),
            errorMessage: error.message,
          });
        })
        .finally(() => {
          const providerId = _get(samlConfiguration, 'providerId');
          if (signUpCount === 0) {
            const shouldTriggerSAMLOnLoad = _get(
              samlConfiguration,
              'triggerOnLoad',
              false,
            );
            if (shouldTriggerSAMLOnLoad && !hasResult) {
              dispatch(processing(true));

              const provider = new firebase.auth.SAMLAuthProvider(providerId);
              firebase.auth().signInWithRedirect(provider);
            }
          }
        });
    }
  }, [dispatch, enableAutomatic, firebase, samlConfiguration, signUpCount]);

  const handleClick = useCallback(() => {
    dispatch(processing(true));
    dispatch(incrementSignUpCount());

    const providerId = _get(samlConfiguration, 'providerId');
    const provider = new firebase.auth.SAMLAuthProvider(providerId);
    const firestore = firebase.firestore();
    firebase
      .auth()
      .signInWithPopup(provider)
      .then(userCredential =>
        handleUserCredentials(userCredential, firestore, samlConfiguration),
      )
      .catch(error => {
        Mixpanel.track('SAML Authentication - Failed', {
          errorCode: _get(error, 'code', 'undefined'),
        });
      })
      .finally(() => {
        dispatch(processing(false));
      });
  }, [dispatch, firebase, samlConfiguration]);

  return handleClick;
}

export default useSAMLAuthentication;
